import { bindDialog, BindDialogReturn, DialogProps, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { MenuItem } from "@mui/material";
import { addDays } from "date-fns";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WaterLossCauseEnum } from "../../common/enums/WaterLossCauseEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { getObjectHistoryItemTypeName, IObjectHistoryWaterLossEventItem, ObjectHistoryItemTypeEnum } from "../../queries/object-history/Common";
import { useSaveHistoryItem } from "../../queries/object-history/UseSaveHistoryItem";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import i18n from "../../services/localization-service/i18n";
import FormDateTimePicker from "../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../forms/form-input/FormInput";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type WaterLossEventEditDialogInitValues = IObjectHistoryWaterLossEventItem;

export type WaterLossEventEditDialogProps = {
    groupID: number;
    contextObject: IeLynxObject;
} & DialogProps<WaterLossEventEditDialogInitValues>;

type WaterLossEventDialogFormValuesType = {
    startTime: Date;
    endTime: Date;
    
    estimatedLoss: number | undefined;
    estimatedLossUOM: number | undefined;

    cause: WaterLossCauseEnum | -1;
    
    notes: string | undefined;
};

const waterLossCauses = [{
    name: i18n.t("enums.waterLossCause.lineBreak3rdParty"),
    value: WaterLossCauseEnum.LineBreak3rdParty
}, {
    name: i18n.t("enums.waterLossCause.lineBreakNon3rdParty"),
    value: WaterLossCauseEnum.LineBreakNon3rdParty
}, {
    name: i18n.t("enums.waterLossCause.unauthorizedUsage"),
    value: WaterLossCauseEnum.UnauthorizedUsage
}, {
    name: i18n.t("enums.waterLossCause.fireDepartment"),
    value: WaterLossCauseEnum.FireDepartment
}, {
    name: i18n.t("enums.waterLossCause.systemMaintenance"),
    value: WaterLossCauseEnum.SystemMaintenance
}, {
    name: i18n.t("enums.waterLossCause.other"),
    value: WaterLossCauseEnum.Other
}];

export default function WaterLossEventEditDialog({
    open,
    onClose,
    values,
    groupID,
    contextObject
}: WaterLossEventEditDialogProps): JSX.Element {
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();

    const isAddMode = !values?.id;

    const { mutate: saveWaterLossEvent/*, status: saveWaterLossEventStatus*/ } = useQueryNotifications(useSaveHistoryItem)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: contextObject,
        offline: offlineState.offline,
        onSuccess: (_data, variables) => {
            const itemName = getObjectHistoryItemTypeName(variables);
            acknowledge(t("saveSuccessMessage", { itemName: itemName }));
        }
    });

    const form = useForm<WaterLossEventDialogFormValuesType>({
        mode: "onChange",
        defaultValues: {
            startTime: new Date(),
            endTime: new Date(),
            estimatedLoss: undefined,
            estimatedLossUOM: undefined,
            cause: -1,
            notes: ""
        } as WaterLossEventDialogFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset
    } = form;

    useEffect(() => {
        const now = new Date();

        reset({
            startTime: values?.startTime ?? addDays(now, -1),
            endTime: values?.endTime ?? now,
            estimatedLoss: values?.estimatedLoss,
            estimatedLossUOM: values?.estimatedLossUOM,
            cause: values?.cause,
            notes: values?.notes
        });
    }, [reset, values]);

    async function onOkay(data: WaterLossEventDialogFormValuesType) {
        const eventItem = {
            ...(values ?? {}),
            startTime: data.startTime,
            endTime: data.endTime,
            estimatedLoss: data.estimatedLoss,
            estimatedLossUOM: data.estimatedLossUOM,
            cause: data.cause,
            notes: data.notes,
            type: ObjectHistoryItemTypeEnum.WaterLossEvent,
            objectID: contextObject.id,
            objectType: contextObject.objectType,
            objectName: contextObject.displayText
        } as IObjectHistoryWaterLossEventItem;

        saveWaterLossEvent(eventItem);

        reset();

        onClose && onClose({
            reason: "ok"
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                "reason": "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    return (
        <>
            <ResponsiveDialog
                title={isAddMode ? t("waterLossEventEditDialog.addEvent") : t("waterLossEventEditDialog.editEvent")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>
                    <FormDateTimePicker
                        name="startTime"
                        label={t("datetime.startTime")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />

                    <FormDateTimePicker
                        name="endTime"
                        label={t("datetime.endTime")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />

                    <FormInput
                        name="estimatedLoss"
                        label={t("waterLossEventEditDialog.estimatedLoss")}
                        sx={{
                            marginTop: 2
                        }}
                        numeric
                        rules={{
                            required: true
                        }}
                    />

                    <FormSelect
                        name="cause"
                        label={t("waterLossEventEditDialog.cause")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    >
                        <MenuItem key={-1} value={-1} disabled={true}>({t("waterLossEventEditDialog.selectCause")})</MenuItem>

                        {waterLossCauses?.map(o =>
                            <MenuItem key={o.value} value={o.value}>{o.name}</MenuItem>
                        )}
                    </FormSelect>

                    <FormInput
                        name="notes"
                        label={t("waterLossEventEditDialog.notes")}
                        multiline={true}
                        rows={6}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: false
                        }}
                    />
                </Form>
            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type WaterLossEventEditDialogState = DialogState<WaterLossEventEditDialogInitValues>;
export type BindWaterLossEventEditDialogReturn = BindDialogReturn<WaterLossEventEditDialogInitValues>;

export function useWaterLossEventEditDialogState(): WaterLossEventEditDialogState {
    return useDialogState();
}

export function bindWaterLossEventEditDialog(props: WaterLossEventEditDialogState): BindWaterLossEventEditDialogReturn {
    return bindDialog(props);
}